import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAdminAuth } from "../contexts/AdminAuthContext";

interface AdminProtectedRouteProps {
  children: ReactNode;
  requiredPermission?: string;
}

export default function AdminProtectedRoute({
  children,
  requiredPermission,
}: AdminProtectedRouteProps) {
  const { user, loading, isAdmin, permissions, twoFactor } = useAdminAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-pulse text-gray-400">Loading...</div>
      </div>
    );
  }

  // Not authenticated → login
  if (!user) {
    return (
      <Navigate
        to="/login"
        state={{ from: location.pathname + location.search }}
        replace
      />
    );
  }

  if (twoFactor.pendingVerification) {
    return <Navigate to="/verify-2fa" replace />;
  }


  // Authenticated but not admin → access denied
  if (!isAdmin) {
    return <Navigate to="/access-denied" replace />;
  }

  // Admin but missing required permission → access denied
  if (requiredPermission && !permissions.includes(requiredPermission)) {
    return <Navigate to="/access-denied" replace />;
  }

  return <>{children}</>;
}
